import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import uniqBy from "lodash/uniqBy";
import { CONVERSATION_TYPES, CONVERSATIONS_TYPES, FAN, FILE, REQUEST_LOADING_STATES } from "../../helpers/constant";
import { IConversation, IConversationData, IGalleryMediaData, IMessage, IMessageData, OnlineUsers } from "../../helpers/types";
import { authService, mediapushService, messageService } from "../../services";
import { chatScriptService } from "../../services/chat-script.service";
import { createPersonalizedMedia } from "../mediaPush/mediaPushSlice";
import { StoreState } from "../store";

interface MessagesState {
  conversationsData: IConversationData[];
  unreadConversationsData: IConversationData[];
  unansweredConversationsData: IConversationData[];
  mediaPushData: IGalleryMediaData[];
  messagesData: IMessageData[];
  conversations: IConversation[];
  searchConversations: IConversationData[];
  selectedConversation?: IConversation;
  loading: REQUEST_LOADING_STATES;
  conversationLoading: REQUEST_LOADING_STATES;
  error: any;
  onlineUsers: OnlineUsers[];
  totalPages: number;
  currentFilter: string;
  unreadConversations: number;
  searchValue: string;
  type: string;
  isSearching: boolean;
  unreadTypes: { [key: string]: boolean };
  findAllMessagesByConversationIdLoading: REQUEST_LOADING_STATES;
}

type DeleteMessagePayload = {
  messageId: string;
  conversationId?: string;
  hasNewMessages?: boolean;
  lastMessage?: {
    sentBy: string;
    text?: string;
    isAudio?: boolean;
  };
};

const initialState = {
  conversationsData: [],
  unreadConversationsData: [],
  unansweredConversationsData: [],
  messagesData: [],
  mediaPushData: [],
  conversations: [],
  searchConversations: [],
  selectedConversation: undefined,
  loading: REQUEST_LOADING_STATES.IDLE,
  conversationLoading: REQUEST_LOADING_STATES.IDLE,
  error: "",
  onlineUsers: [],
  totalPages: -1,
  currentFilter: "ALL",
  unreadConversations: 0,
  searchValue: "",
  type: "GLOBAL",
  isSearching: false,
  unreadTypes: {},
  findAllMessagesByConversationIdLoading: REQUEST_LOADING_STATES.IDLE,
} as MessagesState;

const isAudioMessageType = (message?: IMessage): boolean => {
  if (!message?.medias?.length) return false;

  return message.medias.some((media) => {
    const mediaUrl = (media?.url || "").toLowerCase();
    return (
      media?.isAudio === true ||
      mediaUrl.includes(".mp3") ||
      mediaUrl.includes(".wav") ||
      mediaUrl.includes(".m4a") ||
      mediaUrl.includes(".ogg") ||
      mediaUrl.includes(".aac") ||
      mediaUrl.includes(".webm") ||
      mediaUrl.includes("audio/") ||
      mediaUrl.includes("audio%2f")
    );
  });
};

const getLastMessagePreview = (message?: IMessage) => {
  if (!message) return undefined;

  return {
    text: message.text || undefined,
    sentBy: message.senderId || "",
    isAudio: isAudioMessageType(message),
  };
};

const updateConversationPreviewById = (
  collection: IConversationData[],
  conversationId: string,
  nextPreview: { text?: string; sentBy: string; isAudio?: boolean } | undefined,
  nextUpdatedAt: string | undefined
) => {
  const typeIndex = collection.findIndex((conversationGroup) =>
    conversationGroup.data.some((conversation) => conversation._id?.toString() === conversationId.toString())
  );

  if (typeIndex === -1) return;

  const conversationIndex = collection[typeIndex].data.findIndex((conversation) => conversation._id?.toString() === conversationId.toString());

  if (conversationIndex === -1) return;

  collection[typeIndex].data[conversationIndex].lastMessage = nextPreview;
  if (nextUpdatedAt) {
    collection[typeIndex].data[conversationIndex].updatedAt = nextUpdatedAt;
  }
};

export const getUserConversations = createAsyncThunk(
  "messages/getUserConversations",
  async (payload: { page: number; type: string; callback?: any }) => {
    const response = await messageService.getAllConversations(payload.page, payload.type);

    return { ...response, type: payload.type, page: payload.page };
  }
);

export const getUserUnreadConversations = createAsyncThunk("messages/getUserUnreadConversations", async (payload: { page: number; type: string }) => {
  const response = await messageService.getUnreadConversations(payload.page, payload.type);
  return { ...response, type: payload.type, page: payload.page };
});

export const getUserUnansweredConversations = createAsyncThunk(
  "messages/getUserUnansweredConversations",
  async (payload: { page: number; type: string }) => {
    const response = await messageService.getUnansweredConversations(payload.page, payload.type);
    return { ...response, type: payload.type, page: payload.page };
  }
);

export const getConversationsAfterMediaPush = createAsyncThunk(
  "messages/getConversationsAfterMediaPush",
  async (payload: { page: number; type: string }) => {
    const response = await messageService.getAllConversations(payload.page, payload.type);
    return { ...response, type: payload.type, page: payload.page };
  }
);

export const searchConversation = createAsyncThunk("messages/searchConversation", async (payload: { search: string; page: number; type: string }) => {
  const response = await messageService.searchConversation(payload);

  return { ...response, type: payload.type, page: payload.page };
});

export const getConversationById = createAsyncThunk("messages/getConversationById", async (conversationId: string) => {
  return messageService.getConversationById(conversationId);
});

export const getConversationMessages = createAsyncThunk("messages/getConversationMessages", async (conversationId: string) => {
  return messageService.getConversationById(conversationId);
});

export const getConversationMedias = createAsyncThunk("messages/getConversationMedias", async (payload: { conversationId: string }) => {
  const medias = await mediapushService.getPrivateGalleryMedia(payload.conversationId);
  return { medias, conversationId: payload.conversationId };
});

export const getUnreadByTypes = createAsyncThunk("messages/getUnreadByTypes", async () => {
  return messageService.getUnreadByTypes();
});

export const sendMessage = createAsyncThunk("messages/sendMessage", async (payload: any) => {
  return messageService.sendMessage(payload);
});

export const readAllMessages = createAsyncThunk("messages/readAllMessages", async (payload: string) => {
  return messageService.readAllMessages(payload);
});

export const findAllMessagesByConversationId = createAsyncThunk(
  "messages/findAllMessagesByConversationId",
  async (payload: { conversationId: string; page: number }) => {
    const response = await messageService.findAllMessagesByConversationId(payload.conversationId, payload.page);
    return { ...response, conversationId: payload.conversationId, page: payload.page };
  }
);

export const sendScriptAction = createAsyncThunk("messages/sendScript", async (payload: any) => {
  return chatScriptService.sendScript(payload);
});

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    selectConversation: (state, action) => {
      state.selectedConversation = action.payload;
    },
    setCreatorChatType: (state, action) => {
      if (state.selectedConversation && state.selectedConversation.interlocutor) {
        state.selectedConversation.interlocutor.chat = action.payload;
      }
    },
    setOnlineUsers: (state, action) => {
      state.onlineUsers = action.payload;
    },
    updateConversations: (state, action) => {
      const newConversation = action.payload.conversation;
      newConversation.hasNewMessages = true;
      const index = state.conversationsData.findIndex((conversation) => conversation.type === action.payload.conversationType);
      if (index !== -1) {
        state.conversationsData[index].data = uniqBy([...state.conversationsData[index].data, newConversation], "_id").sort(sortByUpdatedAtDesc);
        state.conversationsData[index].lastDataLoad = new Date().getTime();
      } else {
        //TODO this is an advanced feature that we need change and to implement later if necessary
        // state.conversationsData = [
        //   ...state.conversationsData,
        //   {
        //     type: newConversation.conversationType!,
        //     lastDataLoad: new Date().getTime(),
        //     totalPages: 1,
        //     data: [newConversation],
        //   },
        // ];
      }
    },
    resetUserConversations: (state) => {
      // state.selectedConversation = {};
    },
    clearAllConversations: (state) => {
      state.conversationsData = [];
      state.messagesData = [];
    },
    resetSearchConversations: (state) => {
      state.searchConversations = [];
    },
    setIsSearching: (state, action) => {
      state.isSearching = action.payload;
    },
    resetType: (state) => {
      state.type = CONVERSATIONS_TYPES.GLOBAL;
    },
    readAllMessagesInConversation: (state, action) => {
      const { conversationId, conversationType } = action.payload;
      const index = state.conversationsData.findIndex((conversation) => conversation.type === conversationType);
      if (index !== -1) {
        const conversationIndex = state.conversationsData[index].data.findIndex(
          (conversation) => conversation._id?.toString() === conversationId.toString()
        );
        if (conversationIndex !== -1) {
          const conversationToEdit = state.conversationsData[index].data[conversationIndex];
          conversationToEdit.hasNewMessages = false;
          state.conversationsData[index].data[conversationIndex] = conversationToEdit;
          state.conversationsData[index].lastDataLoad = new Date().getTime();
          state.conversationsData[index].data = state.conversationsData[index].data.sort(sortByUpdatedAtDesc);
        }
      }

      const unreadIndex = state.unreadConversationsData.findIndex((conversation) => conversation.type === conversationType);
      if (unreadIndex !== -1) {
        state.unreadConversationsData[unreadIndex].data = state.unreadConversationsData[unreadIndex].data.filter(
          (conversation) => conversation._id !== conversationId
        );
        state.unreadConversationsData[unreadIndex].lastDataLoad = new Date().getTime();
      }
      //Modified Unread Types management
      const unreadTypesObject: { [key: string]: boolean } = {};
      if (action.payload.unreadTypes && Array.isArray(action.payload.unreadTypes)) {
        action.payload.unreadTypes.forEach((item: { type: string; hasNewMessages: boolean }) => {
          unreadTypesObject[item.type] = item.hasNewMessages;
        });
      }
      state.unreadTypes = unreadTypesObject;
    },
    markMessagesAsRead: (state, action) => {
      const { conversationId, userId } = action.payload;

      const messages = state.messagesData.find((m) => m.conversationId === conversationId)?.messages || [];

      if (messages?.length > 0) {
        messages
          ?.filter((message) => !message.isRead && message.senderId !== userId)
          ?.map((message) => {
            message.isRead = true;
            return message;
          });
      }
    },
    updateConversationHasNewMessages: (state, action) => {
      const payload = action.payload;
      const index = state.conversationsData.findIndex((conversation) => conversation.type === payload.conversationType);
      if (index !== -1) {
        const conversationIndex = state.conversationsData[index].data.findIndex(
          (conversation) => conversation._id?.toString() === payload.conversationId.toString()
        );
        if (conversationIndex !== -1) {
          const conversationToEdit = state.conversationsData[index].data[conversationIndex];
          conversationToEdit.hasNewMessages = false;
        }
      }
    },

    updateConversationsOrder: (state, action) => {
      const { conversationType, result } = action.payload;
      const index = state.conversationsData.findIndex((conversation) => conversation.type === conversationType);
      if (index !== -1) {
        const conversationIndex = state.conversationsData[index].data.findIndex(
          (conversation) => conversation._id?.toString() === result.conversationId?.toString()
        );
        if (conversationIndex !== -1) {
          state.conversationsData[index].data[conversationIndex].lastMessage = { sentBy: authService.getId(), text: result.text };
          state.conversationsData[index].data[conversationIndex].updatedAt = result.updatedAt;
        }
        state.conversationsData[index].data = state.conversationsData[index].data.sort(sortByUpdatedAtDesc);
      }
    },

    updateConversationsMediaPush: (state, action) => {
      const mediaIndex = state.mediaPushData.findIndex((media) => media.conversationId === action.payload.conversationId);
      if (mediaIndex === -1) {
        // const newMedia: IGalleryMediaData = {
        //   conversationId: action.payload.conversationId,
        //   medias: action.payload.medias,
        //   shouldUpdate: false,
        // };
        // state.mediaPushData = [...state.mediaPushData, newMedia];
      } else {
        const mediaToUpdate = state.mediaPushData[mediaIndex];
        mediaToUpdate.medias = [...action.payload.medias, ...state.mediaPushData[mediaIndex].medias];
        state.mediaPushData[mediaIndex] = mediaToUpdate;
      }
    },

    updateMessagesData: (state, action) => {
      const messageIndex = state.messagesData.findIndex((media) => media.conversationId === action.payload.conversationId);
      if (messageIndex === -1) {
        const newMessageData: IMessageData = {
          conversationId: action.payload.conversationId,
          messages: uniqBy([...action.payload.data], "_id").sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              let da: any = new Date(a.createdAt),
                db: any = new Date(b.createdAt);
              return da - db;
            }
            return 1;
          }),
          total: action.payload.total,
          totalPages: action.payload.totalPages,
          page: action.payload.page,
        };
        state.messagesData = [...state.messagesData, newMessageData];
      } else {
        const messageToUpdate = state.messagesData[messageIndex];
        state.messagesData[messageIndex].totalPages = action.payload.totalPages;
        if (state.messagesData[messageIndex].page < action.payload.totalPages) {
          state.messagesData[messageIndex].page = action.payload.page;
        }
        state.messagesData[messageIndex].total = action.payload.total;

        messageToUpdate.messages = uniqBy([...state.messagesData[messageIndex].messages, ...action.payload.data], "_id").sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            let da: any = new Date(a.createdAt),
              db: any = new Date(b.createdAt);
            return da - db;
          }
          return 1;
        });
        state.messagesData[messageIndex] = messageToUpdate;
      }
    },

    handleNewMessageData: (state, action) => {
      const messageIndex = state.messagesData.findIndex((message) => message.conversationId === action.payload.conversationId);
      if (messageIndex === -1) {
        const newMessageData: IMessageData = {
          conversationId: action.payload.conversationId,
          messages: uniqBy([action.payload], "_id").sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              let da: any = new Date(a.createdAt),
                db: any = new Date(b.createdAt);
              return da - db;
            }
            return 1;
          }),
          total: action.payload.total,
          totalPages: action.payload.totalPages,
          page: action.payload.page,
          //oldestMessageId: action.payload._id !== undefined ? action.payload._id.toString() : null,
        };
        state.messagesData = [...state.messagesData, newMessageData];
      } else {
        const messageToUpdate = state.messagesData[messageIndex];
        state.messagesData[messageIndex].totalPages = action.payload.totalPages;
        if (state.messagesData[messageIndex].page < action.payload.totalPages) {
          state.messagesData[messageIndex].page = action.payload.page;
        }
        state.messagesData[messageIndex].total = action.payload.total;
        messageToUpdate.messages = uniqBy([...state.messagesData[messageIndex].messages, action.payload], "_id");

        state.messagesData[messageIndex] = messageToUpdate;
      }
    },

    handleEditMessage: (state, action) => {
      const messageDataIndex = state.messagesData.findIndex((media) => media.conversationId === action.payload.conversationId);
      if (messageDataIndex !== -1) {
        const messageIndex = state.messagesData[messageDataIndex].messages.findIndex((msg) => msg._id === action.payload._id);
        if (messageIndex !== -1) {
          state.messagesData[messageDataIndex].messages[messageIndex] = action.payload;
        }
      }
    },

    handleEditMessageStatus: (state, action) => {
      const messageDataIndex = state.messagesData.findIndex((media) => media.conversationId === action.payload.conversationId);
      if (messageDataIndex !== -1) {
        const messageIndex = state.messagesData[messageDataIndex].messages.findIndex((msg) => msg._id === action.payload._id);
        if (messageIndex !== -1) {
          state.messagesData[messageDataIndex].messages[messageIndex].status = action.payload.status;
        }
      }
    },
    addConversationToUnread: (state, action) => {
      const newConversation = action.payload.conversation;
      newConversation.hasNewMessages = true;
      newConversation.lastMessage = action.payload.conversation.lastMessage;
      const conversationType =
        action.payload.conversationType === CONVERSATION_TYPES.TO_DELIVER ? CONVERSATION_TYPES.TO_DELIVER : CONVERSATION_TYPES.GENERAL;
      const index = state.unreadConversationsData.findIndex((conversation) => conversation.type === conversationType);
      if (index !== -1) {
        // Check if conversation already exists in the array
        const existingConversationIndex = state.unreadConversationsData[index].data.findIndex(
          (conversation) => conversation._id === newConversation._id
        );

        if (existingConversationIndex !== -1) {
          state.unreadConversationsData[index].data[existingConversationIndex] = newConversation;
        } else {
          state.unreadConversationsData[index].data.push(newConversation);
        }
        state.unreadConversationsData[index].data.sort(sortConversationsByUpdatedAtDesc);
        state.unreadConversationsData[index].lastDataLoad = new Date().getTime();
      }
    },

    handleEditAfterFileUploadMessage: (state, action) => {
      const messageDataIndex = state.messagesData.findIndex((media) => media.conversationId === action.payload.conversationId);
      if (messageDataIndex !== -1) {
        let messageIndex = state.messagesData[messageDataIndex].messages.findIndex((msg) =>
          action.payload.temp_id ? msg.temp_id === action.payload.temp_id : msg._id === action.payload._id
        );
        if (messageIndex !== -1) {
          const updatedMessage = { ...action.payload };
          if (updatedMessage.temp_id) {
            delete updatedMessage.temp_id;
          }
          state.messagesData[messageDataIndex].messages[messageIndex] = updatedMessage;
        }
      }

      // const index = state.conversationsData.findIndex((conversation) => conversation.type === result.conversationType);
      // if (index !== -1) {
      //   const conversationIndex = state.conversationsData[index].data.findIndex(
      //     (conversation) => conversation._id?.toString() === result.newMessage.conversationId?.toString()
      //   );
      //   if (conversationIndex !== -1) {
      //     if (result.newMessage.type == TEXT)
      // Handle all cases: text, audio, or both
      const index = state.conversationsData.findIndex((conversation) => conversation.type === action.payload.conversationType);
      if (index !== -1) {
        const conversationIndex = state.conversationsData[index].data.findIndex(
          (conversation) => conversation._id?.toString() === action.payload.conversationId?.toString()
        );
        if (conversationIndex !== -1) {
          state.conversationsData[index].data[conversationIndex].lastMessage = {
            text: action.payload.message.text || undefined,
            sentBy: authService.getId(),
            isAudio: action.payload.message.medias?.some((media: any) => media.isAudio === true) || false,
          };
          state.conversationsData[index].data[conversationIndex].updatedAt = action.payload.message.updatedAt;
        }
        state.conversationsData[index].data = state.conversationsData[index].data.sort(sortByUpdatedAtDesc);
      }
      //   }
      //   state.conversationsData[index].data = state.conversationsData[index].data.sort(sortByUpdatedAtDesc);
      // }
    },

    handleConversationTypeChange: (state, action) => {
      let type: string;

      if (authService.getRole() == FAN) {
        const isSubscribed = action.payload.isFanSubscribed && new Date(action.payload.isFanSubscribed) > new Date();
        type = isSubscribed ? CONVERSATION_TYPES.GENERAL : CONVERSATION_TYPES.SUBSCRIPTION;
      } else {
        type = action.payload.isToDeliver === true ? CONVERSATION_TYPES.GENERAL : CONVERSATION_TYPES.TO_DELIVER;
      }
      const typeIndex = state.conversationsData.findIndex((typeConversation) => typeConversation.type === type);
      if (typeIndex !== -1) {
        const conversationIndex = state.conversationsData[typeIndex].data.findIndex(
          (conversation) => conversation._id === action.payload.conversationId
        );
        if (conversationIndex !== -1) {
          // const conversationToEdit = state.conversationsData[typeIndex].data[conversationIndex];
          // state.conversationsData[typeIndex].data.splice(conversationIndex, 1);
          // if (action.payload.isToDeliver) {
          //   const newTypeIndex = state.conversationsData.findIndex((typeConversation) => typeConversation.type === CONVERSATION_TYPES.TO_DELIVER);
          //   if (newTypeIndex !== -1) {
          //     state.conversationsData[newTypeIndex].data = [conversationToEdit, ...state.conversationsData[newTypeIndex].data];
          //   }
          // } else {
          //   const newTypeIndex = state.conversationsData.findIndex((typeConversation) => typeConversation.type === action.payload.to);
          //   if (newTypeIndex !== -1) {
          //     state.conversationsData[newTypeIndex].data = [conversationToEdit, ...state.conversationsData[newTypeIndex].data];
          //   }
          // }
          // const oldConversations = state.conversationsData[typeIndex].data;
          // const conversationToEdit = oldConversations[conversationIndex];
          const conversationToEdit = state.conversationsData[typeIndex].data.splice(conversationIndex, 1)[0];
          // remove it immutably
          // state.conversationsData[typeIndex].data = [
          //   ...oldConversations.slice(0, conversationIndex),
          //   ...oldConversations.slice(conversationIndex + 1),
          // ];

          let targetType;
          if (authService.getRole() === "FAN") {
            const isSubscribed = action.payload.isFanSubscribed && new Date(action.payload.isFanSubscribed) > new Date();
            targetType = isSubscribed ? CONVERSATION_TYPES.SUBSCRIPTION : CONVERSATION_TYPES.GENERAL;
          } else {
            targetType = action.payload.isToDeliver === true ? CONVERSATION_TYPES.TO_DELIVER : CONVERSATION_TYPES.GENERAL;
          }
          const newTypeIndex = state.conversationsData.findIndex((typeConversation) => typeConversation.type === targetType);
          if (newTypeIndex !== -1) {
            state.conversationsData[newTypeIndex].data.unshift(conversationToEdit);
          }
        }
      }

      const index = state.conversationsData.findIndex((conversation) => conversation.type === CONVERSATION_TYPES.TO_DELIVER);
      if (index !== -1) {
        const conversationIndex = state.conversationsData[index].data.findIndex(
          (conversation) => conversation._id?.toString() === action.payload.conversationId?.toString()
        );
        if (conversationIndex !== -1) {
          state.conversationsData[index].data[conversationIndex].undeliveredMessage = {
            updatedAt: new Date().toISOString(),
          };
        }
      }
    },

    handleNextUndeliveredMessageChange: (state, action) => {
      const typeIndex = state.conversationsData.findIndex((typeConversation) => typeConversation.type === action.payload.type);
      if (typeIndex !== -1) {
        const conversationIndex = state.conversationsData[typeIndex].data.findIndex(
          (conversation) => conversation._id === action.payload.conversationId
        );
        // if (conversationIndex !== -1) {
        //   state.conversationsData[typeIndex].data[conversationIndex].undeliveredMessage = action.payload.undeliveredMessage;
        // }
      }
    },

    removeFromUnanswered: (state, action: PayloadAction<{ conversationId: string; type: string }>) => {
      const { conversationId, type } = action.payload;
      const typeIndex = state.unansweredConversationsData.findIndex((c) => c.type === type);
      if (typeIndex !== -1) {
        state.unansweredConversationsData[typeIndex].data = state.unansweredConversationsData[typeIndex].data.filter(
          (conv) => conv._id !== conversationId
        );
      }
    },

    removeFromUnread: (state, action: PayloadAction<{ conversationId: string; type: string }>) => {
      const { conversationId, type } = action.payload;
      const typeIndex = state.unreadConversationsData.findIndex((c) => c.type === type);
      if (typeIndex !== -1) {
        state.unreadConversationsData[typeIndex].data = state.unreadConversationsData[typeIndex].data.filter((conv) => conv._id !== conversationId);
      }
    },

    setCurrentFilter: (state, action) => {
      state.currentFilter = action.payload;
    },

    resetCurrentFilter: (state) => {
      state.currentFilter = "ALL";
    },

    updateUnreadConversations: (state, action) => {
      if (action.payload !== state.unreadConversations) {
        state.unreadConversations = action.payload;
      }
      // const unreadTypesObject: { [key: string]: boolean } = {};
      // if (action.payload.unreadTypes && Array.isArray(action.payload.unreadTypes)) {
      //   action.payload.unreadTypes.forEach((item: { type: string; hasNewMessages: boolean }) => {
      //     unreadTypesObject[item.type] = item.hasNewMessages;
      //   });
      // }
      // state.unreadTypes = unreadTypesObject;
    },

    updateUnreadConversationsConditional: (state, action) => {
      // const currentPath = window.location.pathname;
      // const isConversationOpen = currentPath.includes("/fan/requestMedia/") || currentPath.includes("/creator/conversation/");
      // console.log("payload inside " , action.payload)
      // if (!isConversationOpen && action.payload !== state.unreadConversations) {
      state.unreadConversations = action.payload;
      // }
    },

    resetSelectedConversation: (state) => {
      // state.selectedConversation = {};
    },

    setSearchValue: (state, action) => {
      state.searchValue = action.payload;
    },

    updateUnreadTypes: (state, action) => {
      state.unreadTypes = action.payload;
    },

    updateUnreadTypesConditional: (state, action) => {
      const pathname = window.location.pathname;
      const match = pathname.match(/\/(creator|fan)\/(conversation|requestMedia)\/([^\/\?#]+)/);
      const openConversationId = match ? match[3] : null;

      const payloadConversationId = action?.payload?.conversation?._id ?? null;

      const isConversationOpen = Boolean(openConversationId);
      const isSameConversationOpen = Boolean(isConversationOpen && payloadConversationId && openConversationId === payloadConversationId);

      if ((!isConversationOpen || !isSameConversationOpen) && authService.getId() != action.payload.message.senderId) {
        const conversationType = (action.payload as any).conversationType as string | undefined;
        if (conversationType) {
          const currentlyUnread = Boolean(state.unreadTypes?.[conversationType]);
          if (!currentlyUnread) {
            state.unreadTypes = {
              ...state.unreadTypes,
              [conversationType]: true,
            } as { [key: string]: boolean };
          }
          return;
        }
      }
    },

    addMessageInstant: (state, action: PayloadAction<IMessage>) => {
      const { conversationId } = action.payload;
      const messageIndex = state.messagesData.findIndex((msgData) => msgData.conversationId === conversationId);

      if (messageIndex !== -1) {
        state.messagesData[messageIndex].messages.push(action.payload);
      } else {
        const newMessageData: IMessageData = {
          conversationId: conversationId!,
          messages: [action.payload],
          page: 1,
          totalPages: 1,
          total: 1,
        };
        state.messagesData.push(newMessageData);
      }
    },
    addMessage: (state, action) => {
      const messageIndex = state.messagesData.findIndex((message) => message.conversationId === action.payload.conversation._id);
      if (messageIndex === -1) {
        const newMessageData: IMessageData = {
          conversationId: action.payload.conversation._id,
          messages: [action.payload.message],
          total: 1,
          totalPages: 2,
          page: 0,
        };
        state.messagesData = [...state.messagesData, newMessageData];
      } else {
        const messageToUpdate = state.messagesData[messageIndex];
        if (action.payload.message.mediaPushPrice) {
          action.payload.message.type = FILE;
        }
        messageToUpdate.messages = uniqBy([...state.messagesData[messageIndex].messages, action.payload.message], "_id").sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            let da: any = new Date(a.createdAt),
              db: any = new Date(b.createdAt);
            return da - db;
          }
          return 1;
        });
        messageToUpdate.total = messageToUpdate.messages.length;
        state.messagesData[messageIndex] = messageToUpdate;
      }
      const index = state.conversationsData.findIndex((conversation) => conversation.type === action.payload.conversationType);
      if (index !== -1) {
        const conversationIndex = state.conversationsData[index].data.findIndex(
          (conversation) => conversation._id?.toString() === action.payload.conversation._id?.toString()
        );
        if (conversationIndex !== -1) {
          // Handle all cases properly: text, audio, or both
          state.conversationsData[index].data[conversationIndex].lastMessage = {
            text: action.payload.message.text || undefined,
            sentBy: authService.getId(),
            isAudio: action.payload.message.medias?.some((media: any) => media.isAudio === true) || false,
          };
        }
      }
    },
    removeDeletedMessage: (state, action: PayloadAction<DeleteMessagePayload>) => {
      const { messageId, lastMessage, hasNewMessages } = action.payload;
      let targetConversationId = action.payload.conversationId;

      if (!targetConversationId) {
        const existingMessageData = state.messagesData.find((messageData) => messageData.messages.some((message) => message._id === messageId));
        if (existingMessageData) {
          targetConversationId = existingMessageData.conversationId;
        }
      }

      if (!targetConversationId) return;

      const messageDataIndex = state.messagesData.findIndex((messageData) => messageData.conversationId === targetConversationId);
      if (messageDataIndex !== -1) {
        const previousLength = state.messagesData[messageDataIndex].messages.length;
        state.messagesData[messageDataIndex].messages = state.messagesData[messageDataIndex].messages.filter((msg) => msg._id !== messageId);
        if (state.messagesData[messageDataIndex].messages.length !== previousLength) {
          state.messagesData[messageDataIndex].total = Math.max(0, state.messagesData[messageDataIndex].total - 1);
        }
      }

      const remainingMessages = state.messagesData.find((m) => m.conversationId === targetConversationId)?.messages || [];
      const latestMessage = remainingMessages[remainingMessages.length - 1];
      const nextPreview = lastMessage || getLastMessagePreview(latestMessage);
      const nextUpdatedAt = latestMessage?.updatedAt || latestMessage?.createdAt;

      updateConversationPreviewById(state.conversationsData, targetConversationId, nextPreview, nextUpdatedAt);
      updateConversationPreviewById(state.unreadConversationsData, targetConversationId, nextPreview, nextUpdatedAt);
      updateConversationPreviewById(state.unansweredConversationsData, targetConversationId, nextPreview, nextUpdatedAt);
      updateConversationPreviewById(state.searchConversations, targetConversationId, nextPreview, nextUpdatedAt);

      // Update hasNewMessages status if provided by backend
      if (hasNewMessages !== undefined) {
        const updateHasNewMessages = (collection: IConversationData[]) => {
          const typeIndex = collection.findIndex((conversationGroup) =>
            conversationGroup.data.some((conversation) => conversation._id?.toString() === targetConversationId?.toString())
          );
          if (typeIndex !== -1) {
            const conversationIndex = collection[typeIndex].data.findIndex(
              (conversation) => conversation._id?.toString() === targetConversationId?.toString()
            );
            if (conversationIndex !== -1) {
              collection[typeIndex].data[conversationIndex].hasNewMessages = hasNewMessages;
            }
          }
        };
        updateHasNewMessages(state.conversationsData);
        updateHasNewMessages(state.unreadConversationsData);
        updateHasNewMessages(state.unansweredConversationsData);
        updateHasNewMessages(state.searchConversations);
      }
    },
    clearSelectedConversation: (state) => {
      state.selectedConversation = undefined;
    },
    createOrUpdateConversation: (state, action) => {
      const conversationIndex = state.conversationsData.findIndex((conversation) => conversation.type === action.payload.conversationType);
      if (conversationIndex !== -1) {
        const typeData = state.conversationsData[conversationIndex];
        let convIndex = typeData.data.findIndex((conv) => conv._id?.toString() === action.payload.conversation._id?.toString());
        if (convIndex !== -1) {
          state.conversationsData[conversationIndex].data[convIndex].lastMessage = action.payload.conversation.lastMessage;
          state.conversationsData[conversationIndex].data[convIndex].updatedAt = action.payload.conversation.updatedAt;
        } else {
          state.conversationsData[conversationIndex].data.unshift(action.payload.conversation);
          convIndex = 0;
        }
        const currentPath = window.location.pathname;
        if (
          currentPath.includes(`/fan/requestMedia/${action.payload.conversation._id}`) ||
          currentPath.includes(`/creator/conversation/${action.payload.conversation._id}`)
        ) {
          state.conversationsData[conversationIndex].data[convIndex].hasNewMessages = false;
          action.payload.conversation.hasNewMessages = false;
          messageService.readAllMessages(action.payload.conversation._id);
        } else {
          if (authService.getId() != action.payload.message.senderId) {
            state.unreadConversations = action.payload.totalUnread.total;
            state.conversationsData[conversationIndex].data[convIndex].hasNewMessages = true;
            action.payload.conversation.hasNewMessages = true;
          }
        }
        state.conversationsData[conversationIndex].data = state.conversationsData[conversationIndex].data.sort(sortByUpdatedAtDesc);
        // state.conversationsData[conversationIndex].data = uniqBy(state.conversationsData[conversationIndex].data, "_id").sort(sortByUpdatedAtDesc);
      } else {
        const currentPath = window.location.pathname;
        if (
          currentPath.includes(`/fan/requestMedia/${action.payload.conversation._id}`) ||
          currentPath.includes(`/creator/conversation/${action.payload.conversation._id}`)
        ) {
          if (authService.getId() != action.payload.message.senderId) messageService.readAllMessages(action.payload.conversation._id);
        } else if (authService.getId() != action.payload.message.senderId) {
          state.unreadConversations = action.payload.totalUnread.total;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserConversations.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.type = CONVERSATIONS_TYPES.GLOBAL;
      if (action.meta.arg.page === 1) {
        state.isSearching = true;
      }
    });
    builder.addCase(getUserConversations.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.isSearching = false;

      const newConversation: any = action.payload.data.map((conversation) => {
        return conversation;
      });
      const conversationIndex = state.conversationsData.findIndex((conversation) => conversation.type === action.payload.type);
      if (conversationIndex !== -1) {
        state.conversationsData[conversationIndex].data = uniqBy(
          [...state.conversationsData[conversationIndex].data, ...newConversation],
          "_id"
        ).sort(sortByUpdatedAtDesc);
        state.conversationsData[conversationIndex].lastDataLoad = new Date().getTime();
        state.conversationsData[conversationIndex].totalPages = action.payload.totalPages;
        if (state.conversationsData[conversationIndex].page < action.payload.totalPages) {
          state.conversationsData[conversationIndex].page = action.payload.page;
        }
        state.conversationsData[conversationIndex].total = action.payload.total;
      } else {
        state.conversationsData = [
          ...state.conversationsData,
          {
            type: action.payload.type,
            lastDataLoad: new Date().getTime(),
            totalPages: action.payload.totalPages,
            data: newConversation.sort(sortByUpdatedAtDesc),
            total: action.payload.total,
            page: action.payload.page,
          },
        ];
      }
    });
    builder.addCase(getUserConversations.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.isSearching = false;
      state.error = action.error.message;
    });

    builder.addCase(getUserUnreadConversations.pending, (state) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
    });
    builder.addCase(getUserUnreadConversations.fulfilled, (state, action) => {
      const newConversation: any = action.payload.data.map((conversation) => {
        return conversation;
      });
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const conversationIndex = state.unreadConversationsData.findIndex((conversation) => conversation.type === action.payload.type);
      if (conversationIndex !== -1) {
        state.unreadConversationsData[conversationIndex].data = uniqBy(
          [...state.unreadConversationsData[conversationIndex].data, ...newConversation],
          "_id"
        ).sort(sortByUpdatedAtDesc);
        state.unreadConversationsData[conversationIndex].lastDataLoad = new Date().getTime();
        state.unreadConversationsData[conversationIndex].totalPages = action.payload.totalPages;
        if (state.unreadConversationsData[conversationIndex].page < action.payload.totalPages) {
          state.unreadConversationsData[conversationIndex].page = action.payload.page;
        }
        state.unreadConversationsData[conversationIndex].total = action.payload.total;
      } else {
        state.unreadConversationsData = [
          ...state.unreadConversationsData,
          {
            type: action.payload.type,
            lastDataLoad: new Date().getTime(),
            totalPages: action.payload.totalPages,
            data: newConversation.sort(sortByUpdatedAtDesc),
            total: action.payload.total,
            page: action.payload.page,
          },
        ];
      }
    });
    builder.addCase(getUserUnreadConversations.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });

    builder.addCase(getUserUnansweredConversations.pending, (state) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
    });
    builder.addCase(getUserUnansweredConversations.fulfilled, (state, action) => {
      const newConversation: any = action.payload.data.map((conversation) => {
        return conversation;
      });
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      const conversationIndex = state.unansweredConversationsData.findIndex((conversation) => conversation.type === action.payload.type);
      if (conversationIndex !== -1) {
        state.unansweredConversationsData[conversationIndex].data = uniqBy(
          [...state.unansweredConversationsData[conversationIndex].data, ...newConversation],
          "_id"
        ).sort(sortByUpdatedAtDesc);
        state.unansweredConversationsData[conversationIndex].lastDataLoad = new Date().getTime();
        state.unansweredConversationsData[conversationIndex].totalPages = action.payload.totalPages;
        if (state.unansweredConversationsData[conversationIndex].page < action.payload.totalPages) {
          state.unansweredConversationsData[conversationIndex].page = action.payload.page;
        }
        state.unansweredConversationsData[conversationIndex].total = action.payload.total;
      } else {
        state.unansweredConversationsData = [
          ...state.unansweredConversationsData,
          {
            type: action.payload.type,
            lastDataLoad: new Date().getTime(),
            totalPages: action.payload.totalPages,
            data: newConversation.sort(sortByUpdatedAtDesc),
            total: action.payload.total,
            page: action.payload.page,
          },
        ];
      }
    });
    builder.addCase(getUserUnansweredConversations.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });

    builder.addCase(getConversationsAfterMediaPush.pending, (state) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.conversationsData = [];
    });
    builder.addCase(getConversationsAfterMediaPush.fulfilled, (state, action) => {
      const newConversation: any = action.payload.data.map((conversation) => {
        return conversation;
      });

      state.conversationsData = [
        {
          type: action.payload.type,
          lastDataLoad: new Date().getTime(),
          totalPages: action.payload.totalPages,
          data: newConversation.sort(sortByUpdatedAtDesc),
          total: action.payload.total,
          page: action.payload.page,
        },
      ];
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
    });
    builder.addCase(getConversationsAfterMediaPush.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });

    builder.addCase(searchConversation.pending, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
      state.type = CONVERSATIONS_TYPES.SEARCH;
      if (action.meta.arg.page === 1) {
        state.searchConversations = []; //NOTE: TO CLEAR THE CONVERSATIONS ARRAY IF THE USER SEARCH AND TRY TO SEARCH AGAIN
        state.isSearching = true;
      }
    });
    builder.addCase(searchConversation.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.isSearching = false;

      const newConversation: any = action.payload.data.map((conversation) => {
        return conversation;
      });

      const conversationIndex = state.searchConversations.findIndex((conversation) => conversation.type === action.payload.type);
      if (conversationIndex !== -1) {
        state.searchConversations[conversationIndex].data = uniqBy(
          [...state.searchConversations[conversationIndex].data, ...newConversation],
          "_id"
        ).sort(sortByUpdatedAtDesc);
        state.searchConversations[conversationIndex].lastDataLoad = new Date().getTime();
        state.searchConversations[conversationIndex].totalPages = action.payload.totalPages;
        if (state.searchConversations[conversationIndex].page < action.payload.totalPages) {
          state.searchConversations[conversationIndex].page = action.payload.page;
        }
        state.searchConversations[conversationIndex].total = action.payload.total;
      } else {
        state.searchConversations = [
          ...state.searchConversations,
          {
            type: action.payload.type,
            lastDataLoad: new Date().getTime(),
            totalPages: action.payload.totalPages,
            data: newConversation.sort(sortByUpdatedAtDesc),
            total: action.payload.total,
            page: action.payload.page,
          },
        ];
      }
    });
    builder.addCase(searchConversation.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.isSearching = false;
      state.error = action.error.message;
    });

    builder.addCase(getConversationById.fulfilled, (state, action) => {
      if (state.selectedConversation?._id) {
        if (state.selectedConversation._id === action.payload._id) {
          state.selectedConversation = action.payload;
        }
      } else {
        state.selectedConversation = action.payload;
      }
    });

    builder.addCase(getConversationById.rejected, (state, action) => {
      state.error = action.error.message;
    });

    builder.addCase(getConversationMedias.pending, (state) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
    });
    builder.addCase(getConversationMedias.fulfilled, (state, action) => {
      const conversationIndex = state.mediaPushData.findIndex((media) => media.conversationId === action.payload.conversationId);
      if (conversationIndex === -1) {
        const newMedia: IGalleryMediaData = {
          conversationId: action.payload.conversationId,
          medias: action.payload.medias,
          shouldUpdate: false,
        };
        state.mediaPushData = [...state.mediaPushData, newMedia];
      } else {
        //TODO: the rest of logic
      }
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
    });
    builder.addCase(getConversationMedias.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });
    builder.addCase(getConversationMessages.pending, (state, action) => {
      if (state.selectedConversation?._id !== action.meta.arg) {
        state.findAllMessagesByConversationIdLoading = REQUEST_LOADING_STATES.PENDING;
      }
    });
    builder.addCase(getConversationMessages.fulfilled, (state, action) => {
      state.findAllMessagesByConversationIdLoading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.selectedConversation = action.payload;
    });
    builder.addCase(getConversationMessages.rejected, (state, action) => {
      state.findAllMessagesByConversationIdLoading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });
    builder.addCase(getUnreadByTypes.pending, (state) => {
      state.loading = REQUEST_LOADING_STATES.PENDING;
    });
    builder.addCase(getUnreadByTypes.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      // Transform API response array into object format
      const unreadTypesObject: { [key: string]: boolean } = {};
      if (action.payload && Array.isArray(action.payload)) {
        action.payload.forEach((item: { type: string; hasNewMessages: boolean }) => {
          unreadTypesObject[item.type] = item.hasNewMessages;
        });
      }
      state.unreadTypes = unreadTypesObject;
    });
    builder.addCase(getUnreadByTypes.rejected, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });

    builder.addCase(sendMessage.pending, (state) => {});
    builder.addCase(sendMessage.fulfilled, (state, action) => {
      const { temp_id, result } = action.payload;
      const conversationId = result?.conversation._id;
      const ConversationIndex = conversationId != null ? state.messagesData.findIndex((m) => m.conversationId === conversationId) : -1;
      const messagesDataIndex =
        ConversationIndex !== -1 ? ConversationIndex : state.messagesData.findIndex((m) => m.messages?.some((mm: any) => mm?.temp_id === temp_id));

      if (messagesDataIndex === -1) return;

      const messages = state.messagesData[messagesDataIndex].messages as any[];
      const msgIndex = messages.findIndex((m) => {
        return m?.temp_id === temp_id;
      });

      if (msgIndex === -1) return;

      if (result.messageStatus) {
        const messageDataIndex = state.messagesData.findIndex((media) => media.conversationId === result.messageStatus.conversationId);
        if (messageDataIndex !== -1) {
          const messageIndex = state.messagesData[messageDataIndex].messages.findIndex((msg) => msg._id === result.messageStatus._id);
          if (messageIndex !== -1) {
            state.messagesData[messageDataIndex].messages[messageIndex].status = result.messageStatus.status;
          }
        }
      }

      //TODO: FIND A BETTER WAY ESPECIALLY FOR QUICK FILES, NOW IT'S WORKING
      if (temp_id) {
        messages[msgIndex] = result.newMessage;
      } else messages.push(result.newMessage);

      const index = state.conversationsData.findIndex((conversation) => conversation.type === result.conversationType);
      if (index !== -1) {
        const conversationIndex = state.conversationsData[index].data.findIndex(
          (conversation) => conversation._id?.toString() === result.newMessage.conversationId?.toString()
        );
        if (conversationIndex !== -1) {
          // Handle all cases properly: text, audio, or both
          state.conversationsData[index].data[conversationIndex].lastMessage = {
            text: result.newMessage.text,
            sentBy: authService.getId(),
            isAudio: result.newMessage.medias?.some((media: any) => media.isAudio === true),
          };
          state.conversationsData[index].data[conversationIndex].updatedAt = result.newMessage.updatedAt;
        }
        state.conversationsData[index].data = state.conversationsData[index].data.sort(sortByUpdatedAtDesc);
      }
    });
    builder.addCase(sendMessage.rejected, (state, action) => {});

    builder.addCase(readAllMessages.fulfilled, (state, action) => {
      state.unreadConversations = action.payload.total;
      const unreadTypesObject: { [key: string]: boolean } = {};
      if (action.payload.unreadTypes && Array.isArray(action.payload.unreadTypes)) {
        action.payload.unreadTypes.forEach((item: { type: string; hasNewMessages: boolean }) => {
          unreadTypesObject[item.type] = item.hasNewMessages;
        });
      }
      state.unreadTypes = unreadTypesObject;
    });

    builder.addCase(findAllMessagesByConversationId.pending, (state) => {
      state.conversationLoading = REQUEST_LOADING_STATES.PENDING;
      state.findAllMessagesByConversationIdLoading = REQUEST_LOADING_STATES.PENDING;
    });
    builder.addCase(findAllMessagesByConversationId.fulfilled, (state, action) => {
      state.loading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.conversationLoading = REQUEST_LOADING_STATES.SUCCEEDED;
      state.findAllMessagesByConversationIdLoading = REQUEST_LOADING_STATES.SUCCEEDED;
      const messageIndex = state.messagesData.findIndex((media) => media.conversationId === action.payload.conversationId);
      if (messageIndex === -1) {
        const newMessageData: IMessageData = {
          conversationId: action.payload.conversationId,
          messages: uniqBy([...action.payload.data], "_id").sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              let da: any = new Date(a.createdAt),
                db: any = new Date(b.createdAt);
              return da - db;
            }
            return 1;
          }),
          total: action.payload.total,
          totalPages: action.payload.totalPages,
          page: action.payload.page,
        };
        state.messagesData = [...state.messagesData, newMessageData];
      } else {
        const messageToUpdate = state.messagesData[messageIndex];
        state.messagesData[messageIndex].totalPages = action.payload.totalPages;
        if (state.messagesData[messageIndex].page < action.payload.totalPages) {
          state.messagesData[messageIndex].page = action.payload.page;
        }
        state.messagesData[messageIndex].total = action.payload.total;

        messageToUpdate.messages = uniqBy([...state.messagesData[messageIndex].messages, ...action.payload.data], "_id").sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            let da: any = new Date(a.createdAt),
              db: any = new Date(b.createdAt);
            return da - db;
          }
          return 1;
        });
        state.messagesData[messageIndex] = messageToUpdate;
      }
    });
    builder.addCase(findAllMessagesByConversationId.rejected, (state, action) => {
      state.conversationLoading = REQUEST_LOADING_STATES.FAILED;
      state.error = action.error.message;
    });

    builder.addCase(createPersonalizedMedia.fulfilled, (state, action: any) => {
      const { newMessage, messageStatus } = action.payload;
      if (newMessage && newMessage.conversationId) {
        const messageIndex = state.messagesData.findIndex((msgData) => msgData.conversationId === newMessage.conversationId);
        if (messageIndex !== -1) {
          const messageToUpdate = state.messagesData[messageIndex];
          messageToUpdate.messages = uniqBy([...messageToUpdate.messages, { ...newMessage, type: "FILE" }], "_id").sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              let da: any = new Date(a.createdAt),
                db: any = new Date(b.createdAt);
              return da - db;
            }
            return 1;
          });
          messageToUpdate.total = messageToUpdate.messages.length;
          state.messagesData[messageIndex] = messageToUpdate;
        }
      }

      const messageDataIndex = state.messagesData.findIndex((media) => media.conversationId === newMessage.conversationId);
      if (messageDataIndex !== -1) {
        const messageIndex = state.messagesData[messageDataIndex].messages.findIndex((msg) => msg._id === messageStatus._id);
        if (messageIndex !== -1) {
          state.messagesData[messageDataIndex].messages[messageIndex].status = messageStatus.status;
        }
      }
      const conversationIndex = state.conversationsData.findIndex((conversation) => conversation.type === action.payload.conversationType);
      if (conversationIndex !== -1) {
        const typeData = state.conversationsData[conversationIndex];
        let convIndex = typeData.data.findIndex((conv) => conv._id?.toString() === action.payload.conversation._id?.toString());
        if (convIndex !== -1) {
          state.conversationsData[conversationIndex].data[convIndex].lastMessage = {
            text: action.payload.newMessage.text,
            sentBy: authService.getId(),
          };
          state.conversationsData[conversationIndex].data[convIndex].updatedAt = action.payload.newMessage.updatedAt;
        }
        state.conversationsData[conversationIndex].data = state.conversationsData[conversationIndex].data.sort(sortByUpdatedAtDesc);
      }
    });
    builder.addCase(sendScriptAction.fulfilled, (state, action: any) => {
      const { temp_id } = action.meta.arg;
      const conversationId = action.payload.newMessage.conversationId;
      const ConversationIndex = conversationId != null ? state.messagesData.findIndex((m) => m.conversationId === conversationId) : -1;
      const messagesDataIndex =
        ConversationIndex !== -1 ? ConversationIndex : state.messagesData.findIndex((m) => m.messages?.some((mm: any) => mm?.temp_id === temp_id));

      const messages = state.messagesData[messagesDataIndex].messages as any[];
      const msgIndex = messages.findIndex((m) => m?.temp_id === temp_id);
      if (temp_id) {
        messages[msgIndex] = action.payload.newMessage;
      } else messages.push(action.payload.newMessage);

      const index = state.conversationsData.findIndex((conversation) => conversation.type === action.payload.conversationType);
      if (index !== -1) {
        const conversationIndex = state.conversationsData[index].data.findIndex(
          (conversation) => conversation._id?.toString() === action.payload.newMessage.conversationId?.toString()
        );
        if (conversationIndex !== -1) {
          // Handle all cases properly: text, audio, or both
          state.conversationsData[index].data[conversationIndex].lastMessage = {
            text: action.payload.newMessage.text || undefined,
            sentBy: authService.getId(),
            isAudio: action.payload.newMessage.medias?.some((media: any) => media.isAudio === true) || false,
          };
          state.conversationsData[index].data[conversationIndex].updatedAt = action.payload.newMessage.updatedAt;
        }
        state.conversationsData[index].data = state.conversationsData[index].data.sort(sortConversationsByUpdatedAtDesc);
      }
    });
  },
});

const sortByUpdatedAtDesc = (a: IConversation, b: IConversation) => {
  if (a.updatedAt && b.updatedAt) {
    let da: any = new Date(a.updatedAt),
      db: any = new Date(b.updatedAt);
    return db - da;
  }
  return 1;
};

export const isConversationDataExist = (state: StoreState, type: string) => {
  return state.messages.conversationsData.findIndex((conversation) => conversation.type === type) !== -1;
};
const sortConversationsByUpdatedAtDesc = (a: IConversation, b: IConversation) => {
  if (a.updatedAt && b.updatedAt) {
    const da: any = new Date(a.updatedAt),
      db: any = new Date(b.updatedAt);
    return db - da;
  }
  return 1;
};
export const {
  selectConversation,
  setCreatorChatType,
  setOnlineUsers,
  updateConversations,
  readAllMessagesInConversation,
  markMessagesAsRead,
  resetUserConversations,
  updateConversationsOrder,
  updateConversationsMediaPush,
  updateMessagesData,
  handleEditMessageStatus,
  handleNewMessageData,
  handleEditMessage,
  handleEditAfterFileUploadMessage,
  handleConversationTypeChange,
  handleNextUndeliveredMessageChange,
  updateConversationHasNewMessages,
  clearAllConversations,
  removeFromUnanswered,
  removeFromUnread,
  setCurrentFilter,
  resetCurrentFilter,
  updateUnreadConversations,
  updateUnreadConversationsConditional,
  resetSelectedConversation,
  resetSearchConversations,
  setSearchValue,
  setIsSearching,
  updateUnreadTypes,
  updateUnreadTypesConditional,
  addMessageInstant,
  addMessage,
  removeDeletedMessage,
  clearSelectedConversation,
  createOrUpdateConversation,
  addConversationToUnread,
  resetType,
} = messagesSlice.actions;

export default messagesSlice.reducer;
